import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ServiceGroups } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { useServiceByIdQuery } from "@monorepo/services/api/services";
import { nullService } from "@monorepo/slices/service";
import { FormStepsEnum } from "@monorepo/types";
import { Box } from "@mui/material";
import { reverse } from "named-urls";

import { useAppDispatch, useAppSelector } from "hooks";

import { ServiceAccountForm } from "./ServiceAccountForm";
import ServiceFieldsForm from "./ServiceFieldsForm";
import ServicePrepaidForm from "./ServicePrepaidForm";

interface IServiceFormProps {
  id: string;
  ignoreGroup: boolean;
  redirectOnServiceGroupSelect?: (id: string) => string;
  templateId?: string;
  isPaymentBankOnly?: boolean;
}

export const ServiceForm: React.FC<IServiceFormProps> = ({
  id,
  ignoreGroup,
  redirectOnServiceGroupSelect,
  templateId,
  isPaymentBankOnly,
}) => {
  const { data: service } = useServiceByIdQuery(id);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const serviceSlice = useAppSelector((state) => state.serviceSlice);

  useEffect(() => {
    return () => {
      // to force first step on history.back
      dispatch(nullService());
    };
  }, [dispatch]);

  if (service) {
    // firstStep exists in backend service
    const firstStepExists = service.parameters?.find((param) => param.step === FormStepsEnum.ACCOUNT_VERIFICATION);
    // saved firstStep in redux state, used for pdfUpload redirect
    const firstStep = serviceSlice.parameters?.find((param) => param.step === FormStepsEnum.ACCOUNT_VERIFICATION);
    const { group } = service;
    if (group && !ignoreGroup) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "max-content",
            height: "100%",
            overflow: "hidden",
            justifyContent: {
              sm: "center",
            },
          }}
        >
          <ServiceGroups
            group={group}
            onSelectedService={(serviceId) => {
              dispatch(nullService());
              history.push(
                redirectOnServiceGroupSelect
                  ? redirectOnServiceGroupSelect(serviceId)
                  : reverse(routeConfig.dashboard.payments.addService.serviceId, {
                      serviceId,
                    }),
                { ignoreGroup: true }
              );
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "grid",
          height: "100%",
          alignContent: "flex-start",
          overflowY: "auto",
          justifyItems: {
            xs: "stretch",
          },
        }}
      >
        {serviceSlice?.import_date && ((firstStepExists && firstStep && firstStep.value) || !firstStepExists) && (
          <ServicePrepaidForm />
        )}
        {!!firstStepExists && (!firstStep || !firstStep.value) && <ServiceAccountForm id={id} />}
        {((!!firstStepExists && firstStep && firstStep.value) || !firstStepExists) && !serviceSlice?.import_date && (
          <ServiceFieldsForm
            service={!!firstStepExists ? serviceSlice : service}
            templateId={templateId}
            isPaymentBankOnly={isPaymentBankOnly}
          />
        )}
      </Box>
    );
  }
  return null;
};

export default ServiceForm;
